import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';
import { ImageCropperComponent, ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';
import { ModalLayoutModule } from '../modal-layout';

/**
 * Standalone modal that wraps ngx-image-cropper with configurable output size.
 *
 * Opened via `Modal.show({ component: ImageCropperModal, props: { imageBase64 } })`.
 * Dismisses with the cropped image as a Blob, or null if cancelled.
 */
@Component({
    standalone: true,
    imports: [CommonModule, IonicModule, ModalLayoutModule, ImageCropperComponent],
    selector: 'abs-image-cropper-modal',
    template: `
        <abs-modal-layout title="Crop Image" [showClose]="false">
            <div class="cropInfo">
                <div class="cropHint">Drag the image to reposition it inside the crop area.</div>

                <div class="zoomControls">
                    <span class="zoomLabel">Zoom</span>
                    <ion-range [min]="minZoom" max="3" step="0.05" [value]="zoom" (ionInput)="onZoomChange($event)"></ion-range>
                    <span class="zoomValue">{{ zoom | number: '1.0-2' }}×</span>
                </div>
            </div>

            <div
                class="cropperWrap"
                [class.dragging]="isDragging"
                (mousedown)="onDragStart($event)"
                (mousemove)="onDragMove($event)"
                (mouseup)="onDragEnd()"
                (mouseleave)="onDragEnd()"
                (touchstart)="onDragStart($event)"
                (touchmove)="onDragMove($event)"
                (touchend)="onDragEnd()"
                (touchcancel)="onDragEnd()"
                (wheel)="onWheel($event)"
            >
                <image-cropper
                    #cropper
                    [imageBase64]="imageBase64"
                    [imageURL]="imageURL"
                    [imageFile]="imageFile"
                    [maintainAspectRatio]="maintainAspectRatio"
                    [aspectRatio]="aspectRatio"
                    [resizeToWidth]="cropWidth"
                    [resizeToHeight]="cropHeight"
                    [onlyScaleDown]="false"
                    format="png"
                    output="blob"
                    [autoCrop]="true"
                    [allowMoveImage]="false"
                    [hideResizeSquares]="true"
                    [transform]="transform"
                    (imageCropped)="onCropped($event)"
                    (loadImageFailed)="onLoadFailed()"
                ></image-cropper>
            </div>

            <div class="actions">
                <ion-button fill="outline" color="medium" (click)="cancel()">Cancel</ion-button>
                <ion-button (click)="confirm()" [disabled]="!croppedBlob">Confirm</ion-button>
            </div>
        </abs-modal-layout>
    `,
    styles: [
        `
            .cropperWrap {
                width: 100%;
                max-height: 60vh;
                overflow: hidden;
                cursor: grab;
                user-select: none;
            }

            .cropperWrap.dragging {
                cursor: grabbing;
            }

            .cropInfo {
                display: flex;
                flex-direction: column;
                gap: 12px;
                margin-bottom: 12px;
            }

            .cropHint {
                font-size: 0.9rem;
                color: var(--ion-color-medium-shade);
            }

            .zoomControls {
                display: grid;
                grid-template-columns: auto minmax(180px, 1fr) auto;
                gap: 12px;
                align-items: center;
            }

            .zoomLabel,
            .zoomValue {
                font-size: 0.9rem;
                white-space: nowrap;
            }

            .zoomValue {
                text-align: right;
                min-width: 48px;
            }

            .actions {
                display: flex;
                justify-content: flex-end;
                gap: 8px;
                padding: 12px 0 0;
            }

            @media (max-width: 640px) {
                .zoomControls {
                    grid-template-columns: 1fr;
                }

                .zoomValue {
                    text-align: left;
                }
            }

            /* Lock the crop box — pointer-events disabled so it doesn't
               interfere with our drag-to-pan handler on the wrapper */
            ::ng-deep image-cropper .ngx-ic-move {
                pointer-events: none;
                cursor: inherit;
            }

            ::ng-deep image-cropper .ngx-ic-overlay {
                cursor: inherit;
            }
        `
    ]
})
export class ImageCropperModal {
    /** Passed as componentProp by the caller */
    imageBase64?: string;
    imageURL?: string;
    imageFile?: File;
    cropWidth = 512;
    cropHeight = 512;
    /** When false the user can drag a free-form crop box of any aspect ratio. Defaults to true (square for avatar/profile photos). */
    maintainAspectRatio = true;
    minZoom = 1;

    croppedBlob: Blob | null = null;
    zoom = 1;
    transform: ImageTransform = { scale: 1, translateH: 0, translateV: 0, translateUnit: 'px' };

    isDragging = false;
    private lastDragPos: { x: number; y: number } | null = null;
    private isPinching = false;
    private pinchStartDist = 0;
    private pinchStartZoom = 1;

    private static pinchDist(touches: TouchList): number {
        const dx = touches[1].clientX - touches[0].clientX;
        const dy = touches[1].clientY - touches[0].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    @ViewChild('cropper') cropperRef: ImageCropperComponent;

    constructor(private modalController: ModalController) {}

    get aspectRatio(): number {
        return this.cropWidth / this.cropHeight;
    }

    get cropperFrameWidth(): number {
        return Math.min(this.cropWidth, 360);
    }

    get cropperFrameHeight(): number {
        return Math.round(this.cropperFrameWidth / this.aspectRatio);
    }

    onCropped(event: ImageCroppedEvent) {
        this.croppedBlob = event.blob ?? null;
    }

    onZoomChange(event: Event) {
        const value = (event as CustomEvent<{ value: number }>).detail?.value;
        if (typeof value === 'number') this.setZoom(value);
    }

    onWheel(event: WheelEvent) {
        // ctrlKey is set by the browser for trackpad pinch gestures
        if (!event.ctrlKey) return;
        event.preventDefault();
        const delta = -event.deltaY * 0.008;
        this.setZoom(this.zoom + delta);
    }

    onDragStart(event: MouseEvent | TouchEvent) {
        if ('touches' in event && event.touches.length === 2) {
            // Two fingers — begin pinch
            this.isPinching = true;
            this.isDragging = false;
            this.lastDragPos = null;
            this.pinchStartDist = ImageCropperModal.pinchDist(event.touches);
            this.pinchStartZoom = this.zoom;
            event.preventDefault();
            return;
        }
        this.isDragging = true;
        this.isPinching = false;
        const pos = 'touches' in event ? event.touches[0] : event;
        this.lastDragPos = { x: pos.clientX, y: pos.clientY };
        event.preventDefault();
    }

    onDragMove(event: MouseEvent | TouchEvent) {
        if ('touches' in event && event.touches.length === 2) {
            if (!this.isPinching) return;
            event.preventDefault();
            const dist = ImageCropperModal.pinchDist(event.touches);
            this.setZoom(this.pinchStartZoom * (dist / this.pinchStartDist));
            return;
        }
        if (!this.isDragging || !this.lastDragPos) return;
        const pos = 'touches' in event ? event.touches[0] : event;
        const dx = (pos.clientX - this.lastDragPos.x) / this.zoom;
        const dy = (pos.clientY - this.lastDragPos.y) / this.zoom;
        this.lastDragPos = { x: pos.clientX, y: pos.clientY };
        this.transform = {
            ...this.transform,
            translateH: (this.transform.translateH ?? 0) + dx,
            translateV: (this.transform.translateV ?? 0) + dy,
            translateUnit: 'px'
        };
    }

    onDragEnd() {
        this.isDragging = false;
        this.isPinching = false;
        this.lastDragPos = null;
    }

    private setZoom(value: number) {
        this.zoom = Math.min(3, Math.max(this.minZoom, value));
        this.transform = { ...this.transform, scale: this.zoom };
    }

    onLoadFailed() {
        this.croppedBlob = null;
    }

    async confirm() {
        if (this.croppedBlob) {
            await this.modalController.dismiss(this.croppedBlob);
        }
    }

    async cancel() {
        await this.modalController.dismiss(null);
    }
}
